import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    Divider,
    CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../lib/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ApprovedCoupen = () => {
    const { couponId } = useParams();
    const navigate = useNavigate();

    const [couponData, setCouponData] = useState({
        AuthorityName: "",
        AuthDt: "",
        DiscAmt: "",
        Status: "",
        ApprovalFlag: "",
        CouponNo: "",
        PromoCodeName: "",
        MobileNo: "",
        AuthId: 0,
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCouponData = async () => {
            if (!couponId) {
                toast.error("No coupon ID provided", { autoClose: 1000 });
                setLoading(false);
                return;
            }

            try {
                const response = await axiosInstance.post(
                    "MstPromoCodeCoupon/retriveMstPromoCodeCoupon",
                    {
                        CouponId: couponId,
                        Flag: "R",
                    }
                );
                const { data: { Status, Data }, } = response;

                if (Status === 0 && Data && Array.isArray(Data) && Data.length > 0) {
                    const data = Data[0];
                    setCouponData({
                        AuthorityName: data.Name || "N/A",
                        AuthDt: data.AuthDt
                            ? new Date(data.AuthDt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })
                            : "N/A",
                        DiscAmt: data.DiscAmt ? `₹${parseFloat(data.DiscAmt).toFixed(2)}` : "0.00",
                        Status: data.Status || "",
                        ApprovalFlag: data.ApprovalFlag || "N",
                        CouponNo: data.CouponNo || "N/A",
                        PromoCodeName: data.PromoCodeName || "N/A",
                        MobileNo: data.MobileNo || "N/A",
                        ResidentName: data.ResidentName || "N/A",
                        Email: data.Email || "Not Registered!",
                        AuthId: data.AuthId || 1,
                    });
                } else {
                    toast.error("No coupon data found", { autoClose: 1000 });
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchCouponData();
    }, [couponId]);

    const handleApprove = async () => {
        try {
            setLoading(true);
            const newApprovalFlag = couponData.ApprovalFlag === "Y" ? "N" : "Y";

            const response = await axiosInstance.post(
                "MstPromoCodeCoupon/UpdateMstPromoCodeCouponApproved",
                {
                    AuthId: couponData.AuthId,
                    CouponNo: couponData.CouponNo,
                    Flag: "",
                    ApprovalFlag: newApprovalFlag,
                    ApprovalBy: parseInt(sessionStorage.getItem("UserId")) || 1,
                }
            );

            const {
                data: { Status, Message },
            } = response;

            if (Status === 0) {
                toast.success(
                    `Coupon ${newApprovalFlag === "Y" ? "approved" : "unapproved"} successfully!`,
                    { autoClose: 1500 }
                );
                setCouponData((prev) => ({ ...prev, ApprovalFlag: newApprovalFlag }));
            } else {
                toast.error(Message || "Failed to update approval", { autoClose: 1000 });
            }
        } catch (error) {
            toast.error("An error occurred while updating approval", { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const getApproveStatusText = () => {
        switch (couponData.ApprovalFlag) {
            case "Y":
                return "Approved";
            case "N":
                return "Unapproved";
            default:
                return "Unapproved";
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f5f5f5", borderRadius: 2, minHeight: 300 }}>
            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                    }}
                >
                    <CircularProgress size={48} />
                </Box>
            ) : (
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        bgcolor: "white",
                        maxWidth: 600,
                        mx: "auto",
                    }}
                >
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: "normal",
                            color: "rgb(30, 58, 138)",
                            textAlign: "center",
                            mb: 1.5,
                        }}
                    >
                        Coupon Details
                    </Typography>
                    <Divider sx={{ mb: 2, bgcolor: "#1976d2" }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Authority:
                                </Typography>
                                <Typography>{couponData.AuthorityName}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Generate Date:
                                </Typography>
                                <Typography>{couponData.AuthDt}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Amount:
                                </Typography>
                                <Typography>{couponData.DiscAmt}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Coupon No:
                                </Typography>
                                <Typography>{couponData.CouponNo}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Promo Code:
                                </Typography>
                                <Typography>{couponData.PromoCodeName}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Mobile No:
                                </Typography>
                                <Typography>{couponData.MobileNo}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Resident:
                                </Typography>
                                <Typography>{couponData.ResidentName}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box display="flex">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                                    Email:
                                </Typography>
                                <Typography>{couponData.Email}</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                    Status:
                                </Typography>

                                {couponData.ApprovalFlag === "Y" && (
                                    <DoneAllIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                                )}
                                {couponData.ApprovalFlag === "N" && (
                                    <CloseIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                                )}

                                <Typography
                                    sx={{
                                        color:
                                            couponData.ApprovalFlag === "Y"
                                                ? "#16a34a"
                                                : "#dc2626",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {getApproveStatusText()}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={
                                couponData.ApprovalFlag === "Y" ? <CloseIcon /> : <CheckCircleIcon />
                            }
                            sx={{
                                bgcolor: couponData.ApprovalFlag === "Y" ? "#dc2626" : "#16a34a",
                                "&:hover": {
                                    bgcolor: couponData.ApprovalFlag === "Y" ? "#b91c1c" : "#15803d",
                                },
                                color: "white",
                                px: 2,
                                py: 1,
                                borderRadius: 6,
                            }}
                            onClick={handleApprove}
                            disabled={loading}
                        >
                            {couponData.ApprovalFlag === "Y" ? "Unapprove" : "Approve"}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<CancelIcon />}
                            sx={{
                                bgcolor: "#dc2626",
                                "&:hover": { bgcolor: "#b91c1c" },
                                color: "white",
                                px: 2,
                                py: 1,
                                borderRadius: 6,
                            }}
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default ApprovedCoupen;